import {Desk, EmployeeRef, Floor, INeighbourhood} from "../../api/Model";
import {associateBy, groupBy} from "../../util/MapUtil";
import {combineCmp, naturalCmpOptions, numberCmp, stringCmp} from "../../util/SortUtil";
import {mapNotNull} from "../../util/ArrayUtil";
import {DeskModel} from "../desks/DeskModel";
import {EmployeeVisitFigure} from "../employee-visit-report/EmployeeVisitReportFilter";
import {Opt} from "../../util/Optional";

export type DeskInfo = {
    type: 'assigned',
    neighbourhood: Opt<INeighbourhood>,
    employee: EmployeeRef,
    visits: EmployeeVisitFigure,
} | {
    type: 'shared',
    neighbourhood: INeighbourhood,
    neighbourhoodSharing: NeighbourhoodSharing,
} | {
    type: 'overflow',
    neighbourhood: Opt<INeighbourhood>,
};

export type NeighbourhoodSharing = {
    noDeskEmployees: EmployeeRef[],
    sharedDesks: Desk[],
    deskDayUsage: number,
    dayCount: number,
    percent: number,
};

export function calculateNeighbourhoodSharing(
    allDesks: readonly Desk[],
    deskModel: DeskModel,
): Map<INeighbourhood['key'], NeighbourhoodSharing> {
    const desksByNeighbourhood = groupBy(
        mapNotNull(allDesks, desk => {
            const neighbourhood = deskModel.neighbourhood(desk);
            return neighbourhood != null ? {neighbourhood, desk} : undefined;
        }),
        ({neighbourhood}) => neighbourhood,
        ({desk}) => desk,
    );

    return associateBy(
        [...desksByNeighbourhood.entries()],
        ([hood]) => hood.key,
        ([hood, hoodDesks]) => {
            const noDeskEmployees = deskModel.neighbourhoodEmployees(hood)
                .filter(employee => deskModel.assignedDesks.desksOf(employee).length === 0);
            const sharedDesks = hoodDesks.filter(desk => !deskModel.assignedDesks.assigneeOf(desk));
            const deskDayUsage = noDeskEmployees.reduce(
                (total, employee) => total + (deskModel.employeeVisitStatsFor(employee)?.workDayVisits ?? 0),
                0,
            );
            const deskDayAvailability = sharedDesks.length * deskModel.visitWorkDayCount;
            return {
                noDeskEmployees,
                sharedDesks,
                deskDayUsage,
                dayCount: deskModel.visitWorkDayCount,
                percent: deskDayAvailability > 0
                    ? Math.round(deskDayUsage * 100 / deskDayAvailability)
                    : Number.POSITIVE_INFINITY,
            };
        },
    );
}

export class FloorModel {
    floorEmployees: EmployeeRef[];
    floorNeighbourhoods: INeighbourhood[];

    private constructor(
        readonly floor: Floor,
        readonly deskModel: DeskModel,
        readonly floorDesks: Desk[],
        private readonly deskInfos: Map<Desk['id'], DeskInfo>,
    ) {
        this.floorNeighbourhoods = Array.from(new Set(mapNotNull(this.floorDesks, desk => deskModel.neighbourhood(desk))))
            .sort(stringCmp(it => it.name));
        this.floorEmployees = this.floorNeighbourhoods.flatMap(hood => deskModel.neighbourhoodEmployees(hood))
            .sort(combineCmp(
                numberCmp(it => it.deactivated ? 1 : -1),
                stringCmp('fullName'),
            ));
    }

    deskInfo(desk: Desk): DeskInfo {
        return this.deskInfos.get(desk.id) ?? {
            type: 'overflow',
            neighbourhood: undefined,
        };
    }

    static allFloors(deskModel: DeskModel, allDesks: readonly Desk[]): readonly FloorModel[] {
        const desksByFloor = groupBy(allDesks, desk => desk.floorId);

        return deskModel.floors
            .map(floor =>
                new FloorModel(
                    floor,
                    deskModel,
                    [
                        ...desksByFloor.get(floor.id) ?? []
                    ].sort(stringCmp('name', false, naturalCmpOptions)),
                    FloorModel.calculateDeskInfo(allDesks, deskModel),
                )
            )
            .sort(stringCmp(it => it.floor.name, true, naturalCmpOptions));
    }

    private static calculateDeskInfo(allDesks: readonly Desk[], deskModel: DeskModel): Map<Desk['id'], DeskInfo> {
        const neighbourhoodSharing = calculateNeighbourhoodSharing(allDesks, deskModel);

        return associateBy(
            allDesks,
            desk => desk.id,
            (desk): DeskInfo => {
                const neighbourhood = deskModel.neighbourhood(desk);
                const deskAssignee = deskModel.assignedDesks.assigneeOf(desk);
                if (deskAssignee) {
                    return {
                        type: 'assigned',
                        neighbourhood: neighbourhood,
                        employee: deskAssignee,
                        visits: deskModel.employeeVisitStatsFor(deskAssignee) ?? {
                            workDayVisits: 0,
                            tenureDayCount: 0,
                            percent: 0,
                        },
                    };
                } else if (neighbourhood) {
                    const neighbourhoodEmployees = deskModel.neighbourhoodEmployees(neighbourhood);
                    if (neighbourhoodEmployees.length > 0) {
                        return {
                            type: 'shared',
                            neighbourhood,
                            neighbourhoodSharing: neighbourhoodSharing.get(neighbourhood.key) ?? {
                                noDeskEmployees: [],
                                sharedDesks: [],
                                deskDayUsage: 0,
                                dayCount: deskModel.visitWorkDayCount,
                                percent: 0,
                            },
                        };
                    } else {
                        return {
                            type: 'overflow',
                            neighbourhood,
                        };
                    }
                } else {
                    return {
                        type: 'overflow',
                        neighbourhood: undefined,
                    };
                }
            },
        );
    }

    static oneFloor(deskModel: DeskModel, allDesks: readonly Desk[], floorId: Floor['id']): FloorModel | undefined {
        const floor = deskModel.floors
            .find(floor => floor.id === floorId);
        if (!floor) {
            return undefined;
        }

        const floorDesks = allDesks
            .filter(desk => desk.floorId === floor.id)
            .sort(stringCmp('name', false, naturalCmpOptions));

        return new FloorModel(floor, deskModel, floorDesks, FloorModel.calculateDeskInfo(allDesks, deskModel));
    }
}
